/**
 * `startProperties` MARKET hand-off — the operating-region / currency / campaign-tracking
 * fields the external marketing site sends alongside locale + consent, e.g.
 *
 *   { "locale": "en", "or": "INT", "currency": "USD",
 *     "cpid": "a4a69919-…", "cid": "2c3c6583-…", "flow": "v3", "consent": { … } }
 *
 *   • `or`       — operating region, an ISO 3166-1 alpha-3-style catalogue code (`INT` International,
 *     `MEX` Mexico, `BRA` Brazil, `ARG`, `CHL`, …).
 *   • `currency` — an ISO-4217 code (`USD`, `MXN`, `BRL`, …); the top-precedence input to
 *     `resolveMintCurrency`, i.e. it IS what the session is minted in when present.
 *   • `cpid`     — campaign price id (a GUID), forwarded to the session mint.
 *   • `cid`      — campaign / click id (a GUID), forwarded to the session mint.
 *
 * This module is deliberately APP-LEVEL (not inside a configurator flow, not under `nova/`) so
 * both the core `session` store slice (which forwards `or`/`cpid`/`cid` on the fresh-session
 * mint — see `store/slices/session.ts`) and the `nova` configurator (which pre-selects the
 * region from `or`) can read it without a store→nova import inversion. It mirrors the
 * self-contained decode in `src/utils/configurator-flow.ts` (same reason: avoid importing a
 * flow's internals).
 *
 * Values are read DURABLE-first: the boot shim (`src/dev.js`) pre-extracts each field into its
 * own `sessionStorage` key so the hand-off survives the auth-gate `/login` redirect (which
 * drops the query string) AND the one-shot raw-`startProperties` stash being consumed by the
 * configurator. The raw param (URL or its stash) is decoded here only as a fallback. Every value
 * is validated (region/currency codes shape-guarded as ISO codes via `CODE_RE`, ids as GUIDs via
 * `GUID_RE`), so a broken/hostile hand-off yields `null`, never a thrown error.
 */

/** Query-param carrying the base64-encoded hand-off (mirrors startProperties). */
const START_PROPERTIES_PARAM = 'startProperties';
/** sessionStorage key the boot shim stashes the RAW `startProperties` param under. */
const START_PROPERTIES_STASH_KEY = 'ows-start-properties';

/** Durable per-field sessionStorage keys pre-extracted by the boot shim (`src/dev.js`).
 *  Literals MIRROR `src/dev.js` — keep both in sync. */
export const START_OR_KEY = 'ows-start-or';
export const START_CURRENCY_KEY = 'ows-start-currency';
export const START_CPID_KEY = 'ows-start-cpid';
export const START_CID_KEY = 'ows-start-cid';

/**
 * SHAPE guard for an operating-region / currency code, NOT an allowlist of specific markets. Both
 * are 3-letter uppercase codes (ISO 3166-1 alpha-3-style regions like `INT`/`MEX`/`BRA`; ISO-4217
 * currencies like `USD`/`MXN`/`BRL`). We deliberately do NOT enumerate the tenant's launched markets
 * here: the backend is the authority on which region/currency is `is_active` (it validates the mint
 * and `/set-currency`) and the `/configurator` catalogue maps region→currency, so launching a new
 * market needs NO change in this file — the value flows through and the backend/catalogue decide.
 * This gate only rejects malformed / hostile input (wrong length, markup, quotes) as defence-in-depth.
 */
const CODE_RE = /^[A-Z]{3}$/;
/** RFC-4122 GUID (any version) — matches the backend's Joi `string.guid` on `cpid`. */
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The decoded, validated market hand-off. Each field is `null` when absent/invalid. */
export interface StartMarketHandoff {
  /** Operating-region code (ISO 3166-1 alpha-3-style, e.g. `INT`/`MEX`/`BRA`) — the catalogue's region code. */
  or: string | null;
  /** Currency code (ISO-4217, e.g. `USD`/`MXN`/`BRL`) — the top-precedence mint currency (`resolveMintCurrency`). */
  currency: string | null;
  /** Campaign price id (GUID) — forwarded on the session mint. */
  cpid: string | null;
  /** Campaign / click id (GUID) — forwarded on the session mint. */
  cid: string | null;
}

const normRegion = (v: unknown): string | null => {
  const s = typeof v === 'string' ? v.trim().toUpperCase() : '';
  return CODE_RE.test(s) ? s : null;
};
const normCurrency = (v: unknown): string | null => {
  const s = typeof v === 'string' ? v.trim().toUpperCase() : '';
  return CODE_RE.test(s) ? s : null;
};
const normGuid = (v: unknown): string | null => {
  const s = typeof v === 'string' ? v.trim() : '';
  return GUID_RE.test(s) ? s : null;
};

function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * UTF-8-safe base64 decode tolerant of URL-safe base64 + missing padding (a `+` in a query
 * string is decoded to a space by `URLSearchParams`). MIRRORS the decode in
 * `configurator-flow.ts` and each flow's `lib/startProperties.ts`.
 */
function decodeBase64Utf8(raw: string): string {
  let b64 = raw.replace(/ /g, '+').replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4;
  if (pad) b64 += '='.repeat(4 - pad);
  const binary = atob(b64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Raw `startProperties` value from the URL, else the boot-time sessionStorage stash. */
function readRawStartProperties(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(START_PROPERTIES_PARAM);
    if (fromUrl) return fromUrl;
  } catch {
    /* URL unparseable — fall through to the stash. */
  }
  return safeSessionGet(START_PROPERTIES_STASH_KEY);
}

/** Decode the raw param and pull the market fields (fallback when durable keys are absent). */
function fromRawParam(): StartMarketHandoff {
  const raw = readRawStartProperties();
  if (!raw || raw.length > 8 * 1024) return { or: null, currency: null, cpid: null, cid: null };
  try {
    const decoded: unknown = JSON.parse(decodeBase64Utf8(raw));
    const o = decoded && typeof decoded === 'object' ? (decoded as Record<string, unknown>) : {};
    return {
      or: normRegion(o.or),
      currency: normCurrency(o.currency),
      cpid: normGuid(o.cpid),
      cid: normGuid(o.cid),
    };
  } catch {
    return { or: null, currency: null, cpid: null, cid: null };
  }
}

/**
 * The validated market hand-off for this session. Reads each durable key first (stashed by the
 * boot shim), decoding the raw `startProperties` param only for any field the boot shim didn't
 * capture. Every field falls back to `null`, so a missing/malformed hand-off is safe to spread.
 */
export function readStartMarketHandoff(): StartMarketHandoff {
  const fallback = fromRawParam();
  return {
    or: normRegion(safeSessionGet(START_OR_KEY)) ?? fallback.or,
    currency: normCurrency(safeSessionGet(START_CURRENCY_KEY)) ?? fallback.currency,
    cpid: normGuid(safeSessionGet(START_CPID_KEY)) ?? fallback.cpid,
    cid: normGuid(safeSessionGet(START_CID_KEY)) ?? fallback.cid,
  };
}

/**
 * `sessionStorage` key latching that the configurator's campaign-PACK pre-select (from the
 * marketing hand-off's `cpid`/`cid`) has been consumed this browser session. Deliberately SEPARATE
 * from the durable `cpid`/`cid` keys above — those stay put because the session mint reads them for
 * customer attribution; this flag is a one-shot guard so the same pack can't re-apply to a LATER
 * brand (the configurator provider re-mounts per brand, resetting its in-memory guard). Best-effort.
 */
export const START_CAMPAIGN_PRESELECT_DONE_KEY = 'ows-start-campaign-preselect-done';

/** True once the `cpid`/`cid` campaign-pack pre-select has been applied or given up this session. */
export function startCampaignPreselectDone(): boolean {
  return safeSessionGet(START_CAMPAIGN_PRESELECT_DONE_KEY) === '1';
}

/** Latch the campaign-pack pre-select as consumed so it can't re-apply to a later brand. */
export function markStartCampaignPreselectDone(): void {
  try {
    sessionStorage.setItem(START_CAMPAIGN_PRESELECT_DONE_KEY, '1');
  } catch {
    /* sessionStorage unavailable — best-effort; the per-mount ref still guards a single mount. */
  }
}

/**
 * `sessionStorage` key latching WHICH hand-off currency has already been applied to the session this
 * browser session (the ISO code, e.g. `BRL`). The mint fixes currency once on a fresh session, but a
 * RESUMED session that later arrives from a different-currency site is switched via `/set-currency`.
 * We latch the applied CODE (not a plain boolean), so a LATER manual currency pick in the account menu
 * is respected — the SAME hand-off currency won't re-apply when the configurator provider remounts —
 * while arriving with a genuinely DIFFERENT currency still switches. Best-effort.
 */
export const START_CURRENCY_APPLIED_KEY = 'ows-start-currency-applied';

/** The hand-off currency already applied to the session this browser session, or `null`. */
export function readStartCurrencyApplied(): string | null {
  return safeSessionGet(START_CURRENCY_APPLIED_KEY);
}

/** Latch `code` as the hand-off currency applied this session, so it isn't re-applied on a remount. */
export function markStartCurrencyApplied(code: string): void {
  try {
    sessionStorage.setItem(START_CURRENCY_APPLIED_KEY, code);
  } catch {
    /* sessionStorage unavailable — best-effort; the per-mount ref still guards a single mount. */
  }
}

/**
 * `sessionStorage` key latching WHICH hand-off operating region has already been applied to the
 * session this browser session (the region code, e.g. `MEX`). The mint / seed pre-selects a region
 * only when NONE is saved (a saved region wins), but a RESUMED session that later arrives from a
 * different-region marketing site is switched via `setCountry`. As with the currency latch, we record
 * the applied CODE (not a boolean) so a LATER manual region pick is respected — the same hand-off
 * region won't re-apply on a provider remount — while arriving with a genuinely DIFFERENT region still
 * switches. Best-effort.
 */
export const START_OR_APPLIED_KEY = 'ows-start-or-applied';

/** The hand-off operating region already applied to the session this browser session, or `null`. */
export function readStartOrApplied(): string | null {
  return safeSessionGet(START_OR_APPLIED_KEY);
}

/** Latch `code` as the hand-off operating region applied this session, so it isn't re-applied on a remount. */
export function markStartOrApplied(code: string): void {
  try {
    sessionStorage.setItem(START_OR_APPLIED_KEY, code);
  } catch {
    /* sessionStorage unavailable — best-effort; the per-mount ref still guards a single mount. */
  }
}

export default readStartMarketHandoff;
